import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { LanguageSelector } from '../components/LanguageSelector';
import SmartImage from '../components/SmartImage';
import { track } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = '/media/landing/logo.webp';

// Media grid shown on Slide 4 (the CTA slide). 3 Mira medias (2 photos + 1
// slightly-blurred video) sit above the quote, 3 Raphael medias below.
// Assets self-hosted under /public/media/landing/ (WebP/H.264 optimized).
const SLIDE4_MEDIA = {
  mira: {
    photo1: '/media/landing/mira-1.webp',
    photo2: '/media/landing/mira-2.webp',
    video:  '/media/landing/mira.mp4',
  },
  raphael: {
    photo1: '/media/landing/raphael-1.webp',
    photo2: '/media/landing/raphael-2.webp',
    video:  '/media/landing/raphael.mp4',
  },
};

// --- Character portraits (re-used from gender/rotation assets) ---
const PORTRAIT = {
  sofia: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_companion-ai-levels/artifacts/ep1buro8_sofia-thumb.webp',
  lucas: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_companion-ai-levels/artifacts/fxqr8tjq_lucas-thumb.webp',
  luna:  'https://customer-assets-gfyr7b9c.emergentagent.net/job_companion-ai-levels/artifacts/st4xd2hl_luna-thumb.webp',
  raphael: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_companion-ai-levels/artifacts/rj7g2206_raphael-thumb.webp',
};

// --- All copy for the 4 slides in 5 languages. EN is the fallback. ---
const T = {
  fr: {
    eyebrow1: '',
    title1a: 'Plus vous discutez, plus votre compagnon ',
    title1b: 'se dévoile\u2026',
    subtitle1: 'Des personnages IA avec photos ultra réalistes.',
    swipeHint: 'Glissez pour découvrir',
    eyebrow2: '',
    title2a: 'Ils se souviennent ',
    title2b: 'de tout.',
    subtitle2: 'Vos confidences, vos petites manies, vos dates qui comptent. Rien ne se perd entre deux conversations.',
    chips: ['Tu détestes les lundis', 'Ton chat : Mochi 🐱', 'Anniversaire : 14 mars', 'Café : noisette ☕', 'Rêve : voir Tokyo'],
    eyebrow3: '',
    title3a: "Vous ne pensiez pas qu'une ",
    title3b: 'IA',
    title3c: ' pouvait faire ça.',
    chat3: [
      { from: 'ai',   kind: 'text',  content: 'Coucou toi 🤍 bien réveillé ?' },
      { from: 'user', kind: 'text',  content: "Mmh… c'est lundi, j'ai zéro motivation 😮‍💨" },
      { from: 'ai',   kind: 'text',  content: 'Je sais que tu détestes les lundis…' },
      { from: 'ai',   kind: 'photo', content: 'Alors voici une petite photo de moi en terrasse pour te redonner le sourire ☕️🤍' },
      { from: 'user', kind: 'text',  content: 'Oh… merci Sofia, ça me touche vraiment 🥹' },
    ],
    inputPlaceholder: 'Écrivez un message tendre…',
    quote4: 'La patience est récompensée. D\u2019abord l\u2019amitié, puis la romance\u2026 et bien plus encore.',
    cta: 'Commencer',
    haveAccount: "J'ai déjà un compte",
  },
  en: {
    eyebrow1: '',
    title1a: 'The more you chat, the more your companion ', title1b: 'reveals\u2026',
    subtitle1: 'AI characters with ultra-realistic photos.',
    swipeHint: 'Swipe to discover',
    eyebrow2: '',
    title2a: 'They remember ', title2b: 'everything.',
    subtitle2: 'Your secrets, your little habits, the dates that matter. Nothing gets lost between two conversations.',
    chips: ['You hate Mondays', 'Your cat: Mochi 🐱', 'Birthday: March 14', 'Coffee: hazelnut ☕', 'Dream: see Tokyo'],
    eyebrow3: '',
    title3a: "You didn't think an ",
    title3b: 'AI',
    title3c: ' could do this.',
    chat3: [
      { from: 'ai',   kind: 'text',  content: 'Hey you 🤍 are you up?' },
      { from: 'user', kind: 'text',  content: "Mmh… it's Monday, zero motivation 😮‍💨" },
      { from: 'ai',   kind: 'text',  content: 'I know how much you hate Mondays…' },
      { from: 'ai',   kind: 'photo', content: "So here's a little photo of me on the terrace to bring back your smile ☕️🤍" },
      { from: 'user', kind: 'text',  content: 'Oh… thank you Sofia, that really touches me 🥹' },
    ],
    inputPlaceholder: 'Write a tender message…',
    quote4: 'Patience is rewarded. First comes friendship, then romance\u2026 and so much more.',
    cta: 'Get started',
    haveAccount: 'I already have an account',
  },
  es: {
    eyebrow1: '',
    title1a: 'Cuanto más hablas, más ', title1b: 'se revela tu compañero',
    subtitle1: 'Personajes IA con fotos ultrarrealistas.',
    swipeHint: 'Desliza para descubrir',
    eyebrow2: '',
    title2a: 'Recuerdan ', title2b: 'todo.',
    subtitle2: 'Tus confidencias, tus pequeñas manías, las fechas que importan. Nada se pierde entre dos conversaciones.',
    chips: ['Odias los lunes', 'Tu gato: Mochi 🐱', 'Cumpleaños: 14 de marzo', 'Café: avellana ☕', 'Sueño: ver Tokio'],
    eyebrow3: '',
    title3a: 'No pensabas que una ',
    title3b: 'IA',
    title3c: ' pudiera hacer esto.',
    chat3: [
      { from: 'ai',   kind: 'text',  content: 'Hola tú 🤍 ¿ya despierto?' },
      { from: 'user', kind: 'text',  content: 'Mmh… es lunes, cero motivación 😮‍💨' },
      { from: 'ai',   kind: 'text',  content: 'Sé que odias los lunes…' },
      { from: 'ai',   kind: 'photo', content: 'Así que aquí va una foto mía en la terraza para devolverte la sonrisa ☕️🤍' },
      { from: 'user', kind: 'text',  content: 'Oh… gracias Sofia, eso me toca de verdad 🥹' },
    ],
    inputPlaceholder: 'Escribe un mensaje tierno…',
    quote4: 'La paciencia tiene su recompensa. Primero la amistad, luego el romance\u2026 y mucho más.',
    cta: 'Empezar',
    haveAccount: 'Ya tengo una cuenta',
  },
  de: {
    eyebrow1: '',
    title1a: 'Je mehr du chattest, desto mehr ', title1b: 'zeigt sich dein Begleiter\u2026',
    subtitle1: 'KI-Charaktere mit ultrarealistischen Fotos.',
    swipeHint: 'Wischen zum Entdecken',
    eyebrow2: '',
    title2a: 'Sie erinnern sich an ', title2b: 'alles.',
    subtitle2: 'Deine Geheimnisse, deine kleinen Eigenheiten, die Daten, die zählen. Zwischen zwei Gesprächen geht nichts verloren.',
    chips: ['Du hasst Montage', 'Deine Katze: Mochi 🐱', 'Geburtstag: 14. März', 'Kaffee: Haselnuss ☕', 'Traum: Tokio sehen'],
    eyebrow3: '',
    title3a: 'Du hättest nicht gedacht, dass eine ',
    title3b: 'KI',
    title3c: ' das kann.',
    chat3: [
      { from: 'ai',   kind: 'text',  content: 'Hey du 🤍 schon wach?' },
      { from: 'user', kind: 'text',  content: 'Mmh… es ist Montag, null Motivation 😮‍💨' },
      { from: 'ai',   kind: 'text',  content: 'Ich weiß, dass du Montage hasst…' },
      { from: 'ai',   kind: 'photo', content: 'Hier ist ein kleines Foto von mir auf der Terrasse, damit du wieder lächelst ☕️🤍' },
      { from: 'user', kind: 'text',  content: 'Oh… danke Sofia, das berührt mich wirklich 🥹' },
    ],
    inputPlaceholder: 'Schreibe eine zärtliche Nachricht…',
    quote4: 'Geduld wird belohnt. Zuerst Freundschaft, dann Romantik \u2026 und noch so viel mehr.',
    cta: 'Loslegen',
    haveAccount: 'Ich habe bereits ein Konto',
  },
  pt: {
    eyebrow1: '',
    title1a: 'Quanto mais vocês conversam, mais seu companheiro ', title1b: 'se revela\u2026',
    subtitle1: 'Personagens IA com fotos ultrarrealistas.',
    swipeHint: 'Deslize para descobrir',
    eyebrow2: '',
    title2a: 'Lembram-se de ', title2b: 'tudo.',
    subtitle2: 'As suas confidências, as suas pequenas manias, as datas que importam. Nada se perde entre duas conversas.',
    chips: ['Detestas as segundas', 'O teu gato: Mochi 🐱', 'Aniversário: 14 de março', 'Café: avelã ☕', 'Sonho: ver Tóquio'],
    eyebrow3: '',
    title3a: 'Não pensava que uma ',
    title3b: 'IA',
    title3c: ' pudesse fazer isto.',
    chat3: [
      { from: 'ai',   kind: 'text',  content: 'Olá tu 🤍 já acordaste?' },
      { from: 'user', kind: 'text',  content: 'Mmh… é segunda-feira, zero motivação 😮‍💨' },
      { from: 'ai',   kind: 'text',  content: 'Eu sei que detestas as segundas…' },
      { from: 'ai',   kind: 'photo', content: 'Então aqui vai uma fotinha minha na esplanada para te devolver o sorriso ☕️🤍' },
      { from: 'user', kind: 'text',  content: 'Oh… obrigada Sofia, isso toca-me mesmo 🥹' },
    ],
    inputPlaceholder: 'Escreva uma mensagem terna…',
    quote4: 'A paciência é recompensada. Primeiro a amizade, depois o romance\u2026 e muito mais.',
    cta: 'Começar',
    haveAccount: 'Já tenho uma conta',
  },
};

// Per-slide duration. The chat-preview slide is longer because it animates a
// full conversation. Order: [chars grid, chat preview, CTA].
const SLIDE_DURATIONS = [5200, 14000, 5200];
const TOTAL_SLIDES = 3;

export default function LandingPage() {
  const { isAuthenticated, login: authLogin } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = T[language] || T.en;

  // ===== Slide state =====
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef(null);

  // Touch handling
  const touchStartX = useRef(null);
  const touchDeltaX = useRef(0);

  // ===== Analytics =====
  useEffect(() => {
    track('home_page_viewed');
  }, []);

  // ===== Auto-advance (stops on last slide, no loop) =====
  useEffect(() => {
    if (paused) return undefined;
    if (activeIndex >= TOTAL_SLIDES - 1) return undefined;
    const id = setTimeout(() => {
      setActiveIndex((i) => i + 1);
    }, SLIDE_DURATIONS[activeIndex]);
    intervalRef.current = id;
    return () => clearTimeout(id);
  }, [activeIndex, paused]);

  // ===== Chat preview slide — Animated chat sequencing =====
  // Every bubble (AI or user) is preceded by a typing indicator (3 animated
  // dots). AI dots appear on the left (beige), user dots on the right
  // (terracotta). The chat body auto-scrolls to keep the latest activity
  // visible. The sequence resets every time this slide becomes active.
  const [chatVisible, setChatVisible] = useState([]); // indices of revealed messages
  const [chatTyping, setChatTyping] = useState(null); // 'ai' | 'user' | null
  const chatScrollRef = useRef(null);

  useEffect(() => {
    setChatVisible([]);
    setChatTyping(null);
    if (activeIndex !== 1) return undefined;

    const timers = [];
    const schedule = (delay, fn) => timers.push(setTimeout(fn, delay));

    // Timeline (ms). Slide duration is 14000ms.
    schedule(500,   () => setChatTyping('ai'));                                   // Sofia typing…
    schedule(2000,  () => { setChatTyping(null); setChatVisible([0]); });          // Sofia bubble 1
    schedule(2700,  () => setChatTyping('user'));                                 // You typing…
    schedule(3700,  () => { setChatTyping(null); setChatVisible([0, 1]); });       // User reply 1
    schedule(4400,  () => setChatTyping('ai'));                                   // Sofia typing…
    schedule(5900,  () => { setChatTyping(null); setChatVisible([0, 1, 2]); });    // Sofia bubble 2
    schedule(6600,  () => setChatTyping('ai'));                                   // Sofia typing photo…
    schedule(8400,  () => { setChatTyping(null); setChatVisible([0, 1, 2, 3]); }); // Sofia photo + caption
    schedule(9200,  () => setChatTyping('user'));                                 // You typing…
    schedule(10300, () => { setChatTyping(null); setChatVisible([0, 1, 2, 3, 4]); });// User reply 2

    return () => timers.forEach(clearTimeout);
  }, [activeIndex]);

  // Auto-scroll the chat panel to the bottom whenever a new message arrives
  // or the typing indicator appears, so the latest activity stays in view.
  useEffect(() => {
    const el = chatScrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [chatVisible, chatTyping]);

  // ===== CTA slide — Staggered media reveal =====
  // When the CTA slide becomes active, only the FIRST photo of each character
  // is visible. After 1s, the SECOND photo fades in. After another 1s, the
  // (slightly blurred) VIDEOS fade in. Each new slot uses a soft opacity
  // transition handled inside <Slide4Media>.
  //   0 → only photo1
  //   1 → photo1 + photo2
  //   2 → photo1 + photo2 + video
  const [slide4Stage, setSlide4Stage] = useState(0);
  useEffect(() => {
    if (activeIndex !== 2) {
      setSlide4Stage(0);
      return undefined;
    }
    setSlide4Stage(0);
    const timers = [
      setTimeout(() => setSlide4Stage(1), 1000),
      setTimeout(() => setSlide4Stage(2), 2000),
    ];
    return () => timers.forEach(clearTimeout);
  }, [activeIndex]);

  const goTo = useCallback((idx) => {
    const next = ((idx % TOTAL_SLIDES) + TOTAL_SLIDES) % TOTAL_SLIDES;
    setActiveIndex(next);
  }, []);

  // ===== Touch swipe handlers (mobile + trackpad) =====
  // On mobile, after a real touch sequence, the browser also fires synthetic
  // `mousedown`/`mouseup` events (~300ms later) for backwards-compat with
  // mouse-only handlers. Without dedup, a single tap would fire onTouchEnd
  // twice and advance the slider by 2. We track the last touch timestamp and
  // ignore mouse events that arrive shortly after.
  const lastTouchAt = useRef(0);
  const gestureHandledAt = useRef(0);

  const beginGesture = (e) => {
    touchStartX.current = e.touches ? e.touches[0].clientX : e.clientX;
    touchDeltaX.current = 0;
    // Note: we no longer pause the auto-advance here. A bare tap must have
    // zero side-effects on the timer. We pause only once a real horizontal
    // drag is detected inside onTouchMove.
  };
  const endGesture = () => {
    const dx = touchDeltaX.current;
    touchStartX.current = null;
    touchDeltaX.current = 0;
    setPaused(false);
    if (Math.abs(dx) > 50) {
      // Swipe gesture → navigate left or right
      if (dx < 0) goTo(activeIndex + 1);
      else goTo(activeIndex - 1);
    }
    // (Tap-to-advance disabled — a bare tap no longer changes slide.)
  };

  const onTouchStart = (e) => {
    lastTouchAt.current = Date.now();
    beginGesture(e);
  };
  const onTouchMove = (e) => {
    if (touchStartX.current == null) return;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    touchDeltaX.current = x - touchStartX.current;
    // First time we see meaningful movement, pause the auto-advance so the
    // slide doesn't jump under the user's finger mid-swipe.
    if (!paused && Math.abs(touchDeltaX.current) > 10) setPaused(true);
  };
  const onTouchEnd = () => {
    lastTouchAt.current = Date.now();
    // Mark this gesture as handled so a synthetic mouseup that slips through
    // the timestamp guard still cannot double-fire.
    const now = Date.now();
    if (now - gestureHandledAt.current < 600) return;
    gestureHandledAt.current = now;
    endGesture();
  };
  const onMouseDown = (e) => {
    // Skip synthetic mousedown that immediately follows a real touch tap.
    if (Date.now() - lastTouchAt.current < 600) return;
    beginGesture(e);
  };
  const onMouseUp = () => {
    if (Date.now() - lastTouchAt.current < 600) return;
    const now = Date.now();
    if (now - gestureHandledAt.current < 600) return;
    gestureHandledAt.current = now;
    endGesture();
  };

  // Helper spread to attach on any interactive element that must NOT trigger
  // the tap-to-advance / swipe handlers on the root (CTA, login link,
  // progress bars, language selector, etc.).
  const stopProp = {
    onMouseDown: (e) => e.stopPropagation(),
    onMouseUp: (e) => e.stopPropagation(),
    onTouchStart: (e) => e.stopPropagation(),
    onTouchEnd: (e) => e.stopPropagation(),
  };

  // ===== CTA =====
  // "Get started" sends the user straight to the gender-selection flow. If
  // the user isn't authenticated yet, we silently create a guest account
  // (no signup form) so they can try the app without friction. They can
  // upgrade to a real account later from Settings.
  const [ctaLoading, setCtaLoading] = useState(false);
  const onCtaClick = async () => {
    if (ctaLoading) return;
    // Set the same guard flag RegisterPage checks on mount, so we don't
    // double-fire `signup_started` once the next page renders.
    try { sessionStorage.setItem('mp_signup_started_fired', '1'); } catch (_) { /* ignore */ }
    track('signup_started');
    if (isAuthenticated) {
      navigate('/select-gender');
      return;
    }
    setCtaLoading(true);
    try {
      const response = await axios.post(`${API}/auth/guest`);
      // Await so localStorage is written and auth state is set before we
      // navigate. Without this the destination page can render before the
      // token is available and bounce the user to /login.
      await authLogin(response.data.access_token, response.data.user);
      navigate('/select-gender');
    } catch (error) {
      console.error('Guest login error:', error);
      if (error?.code === 'STORAGE_UNAVAILABLE') {
        toast.error(
          language === 'fr'
            ? "Impossible d'enregistrer ta session. Désactive le mode navigation privée ou autorise le stockage local, puis réessaie."
            : language === 'es'
              ? 'No se pudo guardar tu sesión. Desactiva el modo privado o permite el almacenamiento local e inténtalo de nuevo.'
              : language === 'de'
                ? 'Sitzung konnte nicht gespeichert werden. Deaktiviere den privaten Modus oder erlaube lokalen Speicher und versuche es erneut.'
                : language === 'pt'
                  ? 'Não foi possível guardar a sessão. Desativa a navegação privada ou permite o armazenamento local e tenta novamente.'
                  : "Couldn't save your session. Please disable private browsing or allow local storage, then try again."
        );
      } else {
        toast.error(language === 'fr' ? 'Erreur de connexion invité' : 'Guest login error');
      }
      setCtaLoading(false);
    }
  };

  return (
    <div
      className="relative w-full h-[100dvh] overflow-hidden select-none"
      style={{
        fontFamily: 'Manrope, sans-serif',
        color: '#3D2E22',
        background: 'radial-gradient(120% 70% at 70% -5%, #FBF1E6 0%, #FAF6F1 50%, #F3EAE0 100%)',
        touchAction: 'pan-y',
      }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      data-testid="landing-page"
    >
      {/* Animated background blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <div
          className="absolute"
          style={{
            top: '-6%', right: '-20%',
            width: '80vw', height: '80vw', maxWidth: 420, maxHeight: 420,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(212,160,136,0.5), rgba(212,160,136,0) 70%)',
            filter: 'blur(6px)',
            animation: 'mcBlob 16s ease-in-out infinite',
          }}
        />
        <div
          className="absolute"
          style={{
            bottom: '-10%', left: '-30%',
            width: '80vw', height: '80vw', maxWidth: 420, maxHeight: 420,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(139,158,126,0.34), rgba(139,158,126,0) 70%)',
            filter: 'blur(6px)',
            animation: 'mcBlob2 19s ease-in-out infinite',
          }}
        />
      </div>

      {/* === Top fixed header: progress bars + logo + language === */}
      <div className="absolute top-0 left-0 right-0 z-40 px-4 pt-3.5">
        <div className="mx-auto max-w-[440px]">
          {/* Progress bars */}
          <div className="flex gap-1.5">
            {Array.from({ length: TOTAL_SLIDES }).map((_, i) => (
              <div
                key={i}
                className="flex-1 h-[3px] rounded-full overflow-hidden cursor-pointer"
                style={{ background: 'rgba(61,46,34,0.15)' }}
                onClick={() => goTo(i)}
                {...stopProp}
                data-testid={`progress-bar-${i}`}
              >
                <div
                  key={`fill-${activeIndex}-${i}`}
                  style={{
                    height: '100%',
                    borderRadius: 3,
                    background: '#C67D5B',
                    // Past slides stay full, future stay empty; active animates 0→100%.
                    width: i < activeIndex ? '100%' : i === activeIndex ? '100%' : '0%',
                    animation: i === activeIndex && !paused
                      ? `mcProgress ${SLIDE_DURATIONS[activeIndex]}ms linear forwards`
                      : undefined,
                  }}
                />
              </div>
            ))}
          </div>
          {/* Logo + language */}
          <div className="flex items-center justify-between gap-2 mt-3">
            <div className="flex items-center gap-2">
              <div className="w-[26px] h-[26px] rounded-lg overflow-hidden" style={{ boxShadow: 'rgba(166,93,63,0.22) 0 3px 10px' }}>
                <img src={LOGO_URL} alt="MyCompanion" className="w-full h-full object-cover block" />
              </div>
              <span style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, fontSize: 14 }}>
                MyCompanion <span style={{ color: '#C67D5B' }}>AI</span>
              </span>
            </div>
            <div {...stopProp}>
              <LanguageSelector />
            </div>
          </div>
        </div>
      </div>

      {/* === Slides container === */}
      <div
        className="absolute inset-0 flex will-change-transform"
        style={{
          width: `${TOTAL_SLIDES * 100}%`,
          transform: `translateX(-${activeIndex * (100 / TOTAL_SLIDES)}%)`,
          transition: 'transform 600ms cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        {/* ---------- SLIDE 1 — Characters grid ---------- */}
        <Slide>
          <div className="h-full flex flex-col px-5 pb-6" style={{ paddingTop: 78 }}>
            <div className="flex-none text-center mb-4">
              {t.eyebrow1 && (
                <div className="text-[11.5px] font-bold tracking-[0.14em] mb-2" style={{ color: '#C67D5B' }}>
                  {t.eyebrow1}
                </div>
              )}
              <h1 style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, fontSize: 30, lineHeight: 1.06, margin: '0 0 8px' }}>
                {t.title1a}
                <em style={{
                  fontStyle: 'italic',
                  background: 'linear-gradient(100deg, #C67D5B, #A65D3F 40%, #C99A43 70%, #8B9E7E)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>
                  {t.title1b}
                </em>
              </h1>
              <p className="text-[13.5px] leading-[1.5] mx-auto max-w-[300px]" style={{ color: '#8C7B6B' }}>
                {t.subtitle1}
              </p>
            </div>
            {/* 2x2 grid */}
            <div className="flex-1 min-h-0 grid grid-cols-2 grid-rows-2 gap-3">
              <CharacterCard name="Sofia" src={PORTRAIT.sofia} objectPosition="center 18%" />
              <CharacterCard name="Lucas" src={PORTRAIT.lucas} objectPosition="center 15%" />
              <CharacterCard name="Luna"  src={PORTRAIT.luna}  objectPosition="center 18%" scan />
              <CharacterCard name="Raphaël" src={PORTRAIT.raphael} objectPosition="center 20%" />
            </div>
            <div className="flex-none flex items-center justify-center gap-2 mt-3.5 text-[12.5px] font-semibold" style={{ color: '#B0A192' }}>
              {t.swipeHint}{' '}
              <span style={{ display: 'inline-block', animation: 'mcNudge 1.4s ease-in-out infinite' }}>›</span>
            </div>
          </div>
        </Slide>

        {/* ---------- SLIDE 2 — Chat preview ---------- */}
        <Slide>
          <div className="h-full flex flex-col justify-center px-5 pb-7" style={{ paddingTop: 78 }}>
            <div className="text-center mb-4">
              {t.eyebrow3 && (
                <div className="text-[11.5px] font-bold tracking-[0.14em] mb-2" style={{ color: '#C67D5B' }}>
                  {t.eyebrow3}
                </div>
              )}
              <h2 style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, fontSize: 28, lineHeight: 1.1, margin: 0 }}>
                {t.title3a}
                <em style={{ fontStyle: 'italic', color: '#C67D5B' }}>{t.title3b}</em>
                {t.title3c}
              </h2>
            </div>
            <div
              className="relative w-full max-w-[340px] mx-auto rounded-[24px] overflow-hidden"
              style={{
                background: 'rgba(255,255,255,0.92)',
                border: '1px solid rgba(255,255,255,0.7)',
                boxShadow: 'rgba(61,46,34,0.16) 0 24px 54px',
                backdropFilter: 'blur(18px)',
              }}
            >
              {/* Chat header */}
              <div className="flex items-center gap-2.5 px-4 py-3.5"
                   style={{ borderBottom: '1px solid rgba(61,46,34,0.07)', background: 'linear-gradient(rgba(245,237,228,0.55), rgba(255,255,255,0))' }}>
                <div className="relative">
                  <SmartImage src={PORTRAIT.sofia} alt="Sofia" className="w-[42px] h-[42px] rounded-full" imgClassName="object-top" />
                  <span className="absolute bottom-0 right-0 w-[11px] h-[11px] rounded-full"
                        style={{ background: '#6B8E4E', border: '2.5px solid #fff' }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, fontSize: 16 }}>Sofia</div>
                </div>
              </div>
              {/* Chat body (animated conversation) */}
              <div
                ref={chatScrollRef}
                className="px-4 py-4 flex flex-col gap-2.5 h-[400px] overflow-y-auto overflow-x-hidden justify-start mc-scrollbar-hide"
              >
                {t.chat3.map((m, i) => {
                  if (!chatVisible.includes(i)) return null;
                  const isAi = m.from === 'ai';
                  if (m.kind === 'photo') {
                    return (
                      <div
                        key={i}
                        className="self-start max-w-[78%] rounded-2xl overflow-hidden flex-shrink-0"
                        style={{
                          background: '#F5EDE4',
                          borderTopLeftRadius: 6,
                          animation: 'mcMsgIn 320ms ease-out both',
                          boxShadow: 'rgba(61,46,34,0.06) 0 4px 12px',
                        }}
                        data-testid="landing-chat-photo"
                      >
                        <SmartImage
                          src="/media/landing/sofia-chat.webp"
                          alt=""
                          className="w-full aspect-[3/4]"
                          imgClassName="object-cover"
                        />
                        <div className="px-3 py-2 text-[12.5px] leading-snug" style={{ color: '#3D2E22' }}>
                          {m.content}
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div
                      key={i}
                      className={`max-w-[80%] px-3.5 py-2 rounded-2xl text-[13.5px] leading-snug flex-shrink-0 ${isAi ? 'self-start' : 'self-end text-white'}`}
                      style={isAi
                        ? { background: '#F5EDE4', color: '#3D2E22', borderTopLeftRadius: 6, animation: 'mcMsgIn 280ms ease-out both' }
                        : { background: 'linear-gradient(135deg, #C67D5B, #A65D3F)', borderTopRightRadius: 6, animation: 'mcMsgIn 280ms ease-out both' }
                      }
                    >
                      {m.content}
                    </div>
                  );
                })}
                {chatTyping && (
                  <div
                    className={`px-3.5 py-2.5 rounded-2xl flex items-center gap-1 flex-shrink-0 ${chatTyping === 'ai' ? 'self-start' : 'self-end'}`}
                    style={chatTyping === 'ai'
                      ? { background: '#F5EDE4', borderTopLeftRadius: 6, animation: 'mcMsgIn 220ms ease-out both' }
                      : { background: 'linear-gradient(135deg, #C67D5B, #A65D3F)', borderTopRightRadius: 6, animation: 'mcMsgIn 220ms ease-out both' }
                    }
                    data-testid={`landing-chat-typing-${chatTyping}`}
                  >
                    {[0, 1, 2].map((d) => (
                      <span
                        key={d}
                        className="rounded-full"
                        style={{
                          width: 6, height: 6,
                          background: chatTyping === 'ai' ? '#8C7B6B' : 'rgba(255,255,255,0.92)',
                          animation: `mcTypingDot 1.2s ease-in-out ${d * 0.18}s infinite`,
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
              {/* Input bar */}
              <div className="flex items-center gap-2.5 px-3.5 py-3" style={{ borderTop: '1px solid rgba(61,46,34,0.07)' }}>
                <div className="flex-1 px-3.5 py-2.5 rounded-full text-[13px]"
                     style={{ background: '#F5EDE4', color: '#B7A795' }}>
                  {t.inputPlaceholder}
                </div>
                <div className="w-[38px] h-[38px] rounded-full flex items-center justify-center text-white"
                     style={{ background: 'linear-gradient(135deg, #C67D5B, #A65D3F)', boxShadow: 'rgba(166,93,63,0.3) 0 6px 16px' }}>
                  →
                </div>
              </div>
            </div>
          </div>
        </Slide>

        {/* ---------- SLIDE 3 — CTA ---------- */}
        <Slide>
          <div className="h-full flex flex-col justify-between text-center px-5 pb-6" style={{ paddingTop: 80 }}>
            {/* Quote */}
            <p
              className="mx-auto max-w-[340px]"
              style={{
                fontFamily: '"Playfair Display", serif',
                fontStyle: 'italic',
                fontWeight: 500,
                fontSize: 20,
                lineHeight: 1.35,
                color: '#3D2E22',
                margin: 0,
              }}
              data-testid="slide4-quote"
            >
              {t.quote4}
            </p>

            {/* Media grids — Mira row, then Raphael row */}
            <div className="flex flex-col gap-3 mt-4">
              {/* Mira row: 2 photos + 1 slightly blurred video */}
              <div className="grid grid-cols-3 gap-2" data-testid="slide4-mira-row">
                <Slide4Media type="image" src={SLIDE4_MEDIA.mira.photo1} alt="Mira" visible={slide4Stage >= 0} />
                <Slide4Media type="image" src={SLIDE4_MEDIA.mira.photo2} alt="Mira" visible={slide4Stage >= 1} />
                <Slide4Media type="video" src={SLIDE4_MEDIA.mira.video}  alt="Mira" visible={slide4Stage >= 2} />
              </div>
              {/* Raphael row: 2 photos + 1 slightly blurred video */}
              <div className="grid grid-cols-3 gap-2" data-testid="slide4-raphael-row">
                <Slide4Media type="image" src={SLIDE4_MEDIA.raphael.photo1} alt="Raphael" visible={slide4Stage >= 0} />
                <Slide4Media type="image" src={SLIDE4_MEDIA.raphael.photo2} alt="Raphael" visible={slide4Stage >= 1} />
                <Slide4Media type="video" src={SLIDE4_MEDIA.raphael.video}  alt="Raphael" visible={slide4Stage >= 2} />
              </div>
            </div>

            {/* CTA */}
            <div className="mt-4">
              <button
                onClick={onCtaClick}
                disabled={ctaLoading}
                {...stopProp}
                data-testid="get-started-button"
                className="flex items-center justify-center gap-2.5 w-full max-w-[320px] mx-auto px-6 py-[16px] rounded-full font-bold text-white disabled:opacity-70"
                style={{
                  background: 'linear-gradient(135deg, #C67D5B, #A65D3F)',
                  fontFamily: 'Manrope, sans-serif',
                  fontSize: 18,
                  boxShadow: 'rgba(166,93,63,0.32) 0 14px 32px',
                  animation: 'mcGlow 3s ease-in-out infinite',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                {t.cta}
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full"
                      style={{ background: 'rgba(255,255,255,0.22)' }}>→</span>
              </button>
            </div>
          </div>
        </Slide>
      </div>

      {/* Inline keyframes — scoped here to keep this component self-contained */}
      <style>{`
        @keyframes mcBlob {
          0%,100% { transform: translate(0,0) scale(1); }
          50% { transform: translate(6%, 8%) scale(1.06); }
        }
        @keyframes mcBlob2 {
          0%,100% { transform: translate(0,0) scale(1); }
          50% { transform: translate(-8%, -6%) scale(1.08); }
        }
        @keyframes mcDrift {
          0%,100% { transform: translate(0,0); }
          50%     { transform: translate(2px,-6px); }
        }
        @keyframes mcNudge {
          0%,100% { transform: translateX(0); opacity: 0.6; }
          50%     { transform: translateX(4px); opacity: 1; }
        }
        @keyframes mcFloat {
          0%,100% { transform: translateY(0); }
          50%     { transform: translateY(-6px); }
        }
        @keyframes mcGlow {
          0%,100% { box-shadow: rgba(166,93,63,0.32) 0 14px 32px; }
          50%     { box-shadow: rgba(166,93,63,0.48) 0 18px 40px; }
        }
        @keyframes mcScanV {
          0%   { transform: translateY(0); }
          100% { transform: translateY(320%); }
        }
        @keyframes mcProgress {
          from { width: 0%; }
          to   { width: 100%; }
        }
        @keyframes mcTypingDot {
          0%, 70%, 100% { transform: translateY(0);    opacity: 0.4; }
          35%           { transform: translateY(-3px); opacity: 1;   }
        }
        @keyframes mcMsgIn {
          from { opacity: 0; transform: translateY(8px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0)   scale(1);    }
        }
        .mc-scrollbar-hide { scrollbar-width: none; -ms-overflow-style: none; }
        .mc-scrollbar-hide::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
}

// Wrapper for one slide. Takes 1/TOTAL_SLIDES of the 300%-wide container.
// The inner phone-frame constrains content to a mobile-app width so the CTA
// button stays visible on desktop viewports (otherwise the aspect-ratio
// media tiles balloon to 800px+ and push the CTA below the fold).
function Slide({ children }) {
  return (
    <section className="flex-none h-full overflow-hidden" style={{ width: `${100 / TOTAL_SLIDES}%` }}>
      <div className="relative h-full mx-auto max-w-[440px]">
        {children}
      </div>
    </section>
  );
}

// Compact media tile used on Slide 4 (the CTA slide). Renders either an image
// or an autoplaying muted-loop video, with an optional light blur (used on
// videos to tease without revealing everything). The `visible` prop controls
// a soft opacity + subtle scale-in fade used to stagger the reveal.
function Slide4Media({ type, src, alt, blur = false, visible = true }) {
  const mediaStyle = {
    filter: blur ? 'blur(3px)' : 'none',
    transform: blur ? 'scale(1.06)' : 'none', // hide blurred edges
  };
  return (
    <div
      className="relative rounded-[14px] overflow-hidden aspect-[3/4]"
      style={{
        background: '#241A13',
        boxShadow: 'rgba(61,46,34,0.18) 0 8px 22px, rgba(61,46,34,0.06) 0 0 0 1px',
        opacity: visible ? 1 : 0,
        transform: visible ? 'scale(1) translateY(0)' : 'scale(0.96) translateY(6px)',
        transition: 'opacity 520ms ease-out, transform 520ms ease-out',
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      {type === 'video' ? (
        <video
          src={src}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover"
          style={mediaStyle}
        />
      ) : (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
          style={mediaStyle}
        />
      )}
    </div>
  );
}

// Character card with photo, name overlay, optional "scan" effect.
function CharacterCard({ name, src, objectPosition, scan = false }) {
  return (
    <div className="relative rounded-[20px] overflow-hidden"
         style={{
           background: '#241A13',
           boxShadow: scan
             ? 'rgba(214,154,90,0.6) 0 0 0 1.5px, rgba(190,110,60,0.34) 0 14px 34px'
             : 'rgba(61,46,34,0.22) 0 14px 36px, rgba(61,46,34,0.06) 0 0 0 1px',
         }}
    >
      <SmartImage
        src={src}
        alt={name}
        className="absolute inset-0"
        imgClassName=""
        style={{ objectPosition: objectPosition || 'center top' }}
      />
      {scan && (
        <>
          <div className="absolute inset-0 pointer-events-none"
               style={{
                 backgroundImage: 'linear-gradient(rgba(255,200,120,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(255,200,120,0.18) 1px, transparent 1px)',
                 backgroundSize: '18px 18px',
                 mixBlendMode: 'screen',
                 opacity: 0.5,
                 zIndex: 3,
               }}
          />
          <div className="absolute left-0 right-0 pointer-events-none"
               style={{
                 top: '-25%', height: '34%',
                 background: 'linear-gradient(rgba(255,200,120,0), rgba(255,200,120,0.32))',
                 borderBottom: '2px solid rgba(255,215,150,0.95)',
                 boxShadow: 'rgba(255,200,120,0.85) 0 0 20px',
                 animation: 'mcScanV 3.4s ease-in-out infinite',
                 zIndex: 3,
               }}
          />
        </>
      )}
      <div className="absolute left-0 right-0 bottom-0 px-3 pb-3 pt-6 z-[6]"
           style={{ background: 'linear-gradient(0deg, rgba(20,14,10,0.85), rgba(20,14,10,0))' }}>
        <div style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, fontSize: 18, color: '#FFF8F2' }}>
          {name}
        </div>
      </div>
    </div>
  );
}
